import { AiOutlineCloseCircle } from "react-icons/ai";
import Modal from "../../common/Modal";
import { useState } from "react";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";
import AdminService from "../../../services/admin.service";
import GeneralInput from "../../common/GeneralInput";
import styled from "styled-components";

type RevisionStatus = 'pending' | 'verified' | 'no_chat_backup' | 'error'
type TicketStatus =
    | 'included_in_account' | 'paid_cash' | 'paid_transfer' | 'paid_at_agency'
    | 'passenger_pays' | 'pending_payment' | 'qr_sent' | 'no_entrance' | 'not_determined'

const STATUS_LABELS: Record<RevisionStatus, { label: string; color: string }> = {
    pending:         { label: 'Pendiente',         color: '#888888' },
    verified:        { label: 'Verificado ✓',      color: '#1e6b3a' },
    no_chat_backup:  { label: 'Sin respaldo chat',  color: '#7B6B00' },
    error:           { label: 'Error',              color: '#c0392b' },
}

const TICKET_LABELS: Record<TicketStatus, string> = {
    included_in_account: 'Incluida en cuenta',
    paid_cash:           'Pagada en efectivo',
    paid_transfer:       'Pagada por transferencia',
    paid_at_agency:      'Pagada en agencia',
    passenger_pays:      'Pasajero lleva efectivo',
    pending_payment:     'Pendiente de pago',
    qr_sent:             'QR enviado por chat',
    no_entrance:         'Sin entrada (tour libre)',
    not_determined:      'No determinado',
}

export default function CreateRevision() {
    const { setMessage, setIsLoading } = useGlobal()
    const { createRevisionData, setCreateRevisionData, setRelationshipRefreshKey } = useAdmin()

    const [status, setStatus] = useState<RevisionStatus>(
        (createRevisionData?.current_status as RevisionStatus) || 'pending'
    )
    const [ticketStatus, setTicketStatus] = useState<TicketStatus | ''>(
        (createRevisionData?.current_ticket_status as TicketStatus) || ''
    )
    const [notes, setNotes] = useState(createRevisionData?.current_notes || '')
    const [chatSource, setChatSource] = useState(createRevisionData?.current_chat_source || '')

    function close() {
        setCreateRevisionData(undefined)
    }

    async function save(e: React.FormEvent) {
        e.preventDefault()
        if (!createRevisionData) return
        setIsLoading(true)
        try {
            await AdminService.createRevision({
                reservation_id: createRevisionData.reservation_id,
                re_traspaso_id: createRevisionData.re_traspaso_id,
                agency_id: createRevisionData.agency_id,
                status,
                ticket_status: ticketStatus || undefined,
                notes: notes || undefined,
                chat_source: chatSource || undefined,
            })
            setMessage({ type: 'success', content: 'Revisión guardada' })
            setTimeout(() => setMessage(undefined), 2500)
            setRelationshipRefreshKey(prev => prev + 1)
            close()
        } catch (error) {
            console.log(error)
            setMessage({ type: 'error', content: 'Error al guardar la revisión' })
            setTimeout(() => setMessage(undefined), 2500)
        } finally {
            setIsLoading(false)
        }
    }

    if (!createRevisionData) return null

    return (
        <Modal onSubmit={save}>
            <div className="close" onClick={close}><AiOutlineCloseCircle/></div>
            <h1>Revisión de traspaso</h1>
            <Info>
                <span className="passenger">{createRevisionData.passenger_name}</span>
                <span className="detail">{createRevisionData.tour_id} · {createRevisionData.date}</span>
            </Info>

            {/* Status */}
            <Label>Estado de revisión</Label>
            <StatusGrid>
                {(Object.keys(STATUS_LABELS) as RevisionStatus[]).map(s => (
                    <StatusBtn
                        key={s}
                        type="button"
                        color={STATUS_LABELS[s].color}
                        active={status === s}
                        onClick={() => setStatus(s)}
                    >
                        {STATUS_LABELS[s].label}
                    </StatusBtn>
                ))}
            </StatusGrid>

            {/* Ticket status */}
            <Label style={{ marginTop: '20px' }}>Estado de la entrada</Label>
            <TicketGrid>
                {(Object.keys(TICKET_LABELS) as TicketStatus[]).map(t => (
                    <TicketBtn
                        key={t}
                        type="button"
                        active={ticketStatus === t}
                        onClick={() => setTicketStatus(prev => prev === t ? '' : t)}
                    >
                        {TICKET_LABELS[t]}
                    </TicketBtn>
                ))}
            </TicketGrid>

            {/* Chat source */}
            <GeneralInput
                label="Fuente del chat (ej: Inti Para Oficina)"
                value={chatSource}
                onChange={e => setChatSource(e.target.value)}
                style={{ marginTop: '20px' }}
            />

            {/* Notes */}
            <Label style={{ marginTop: '16px' }}>Notas</Label>
            <NotesArea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Observaciones adicionales..."
                rows={3}
            />

            <Actions>
                <button type="submit" className="save">Guardar revisión</button>
                <button type="button" className="cancel" onClick={close}>Cancelar</button>
            </Actions>
        </Modal>
    )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Info = styled.div`
    margin-bottom: 20px;
    .passenger {
        display: block;
        font-size: 17px;
        font-weight: 600;
        color: ${p => p.theme.blueDark};
    }
    .detail {
        display: block;
        font-size: 13px;
        color: #888;
        margin-top: 2px;
    }
`

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${p => p.theme.blueLight};
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
`

const StatusGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`

const StatusBtn = styled.button<{ color: string; active: boolean }>`
    padding: 8px 12px;
    border-radius: 6px;
    border: 2px solid ${p => p.color};
    background-color: ${p => p.active ? p.color : 'transparent'};
    color: ${p => p.active ? '#fff' : p.color};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s;
    &:hover { background-color: ${p => p.color}; color: #fff; }
`

const TicketGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`

const TicketBtn = styled.button<{ active: boolean }>`
    padding: 5px 10px;
    border-radius: 20px;
    border: 1px solid ${p => p.active ? p.theme.blueLight : '#ccc'};
    background-color: ${p => p.active ? p.theme.blueLight : 'transparent'};
    color: ${p => p.active ? '#fff' : '#555'};
    font-size: 12px;
    cursor: pointer;
    transition: all .2s;
    &:hover {
        border-color: ${p => p.theme.blueLight};
        color: ${p => p.theme.blueLight};
    }
`

const NotesArea = styled.textarea`
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 10px;
    font-size: 14px;
    resize: vertical;
    font-family: inherit;
    &:focus { outline: none; border-color: ${p => p.theme.blueLight}; }
`

const Actions = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 20px;
    button {
        padding: 8px 20px;
        border-radius: 6px;
        font-size: 15px;
        cursor: pointer;
        border: none;
    }
    .save { background-color: ${p => p.theme.lightGreen}; color: #fff; }
    .cancel { background-color: #ccc; color: #333; }
`
